import * as THREE from 'three';
import { Annotation } from '../Annotation.js';
import { Utils } from '../utils.js';
import { EventDispatcher } from '../EventDispatcher.js';
import { DeviceAnnotation } from '../DeviceAnnotation.js';

export class AnnotationTool extends EventDispatcher {
    constructor(viewer) {
        super();

        this.viewer = viewer;
        this.renderer = viewer.renderer;

        this.sg = new THREE.SphereGeometry(0.000001);
        this.sm = new THREE.MeshNormalMaterial();
        this.s = new THREE.Mesh(this.sg, this.sm);
        this.s.userData.isDeviceSphere = true;

        this.callbacks = {
            cancel: null,
            finish: null,
        };

        this.isInserting = false;

        this.domRoot = this.viewer.renderer.domElement.parentElement;
        this.elCancelInsertion = this.createCancelInsertionButton();
        this.domRoot.appendChild(this.elCancelInsertion);
    }

    startInsertion(args = {}) {
        console.log(args);
        this.isInserting = true;
        let isDone = false; // Flag to prevent position updates after insertion is complete (cancelled or finished)

        this.elCancelInsertion.style.display = '';
        let domElement = this.viewer.renderer.domElement;

        let panoramaImageId = undefined;

        if (this.viewer && this.viewer.scene && this.viewer.scene.images360[0] && this.viewer.scene.images360[0].focusedImage) {
            panoramaImageId = this.viewer.scene.images360[0].focusedImage.file;
        }

        const annotations = this.viewer.scene.annotations;

        let annotation = undefined;

        if (args.tagId) {
            annotation = annotations.children.find((a) => a.uuid === args.tagId);
            annotation.localPosition = annotation.position.clone();
            annotation.localCameraPosition = annotation.cameraPosition.clone();
            annotation.localCameraTarget = annotation.cameraTarget.clone();
        } else if (args.deviceType) {
            annotation = new DeviceAnnotation({
                position: [589748.27, 231444.54, 753.675],
                title: args.title ?? '',
                serialNumber: args.serialNumber ?? '',
                description: args.description ?? ' ',
                deviceId: args.deviceId,
                modelId: args.modelId,
                ifcType: args.ifcType,
            });
        } else {
            annotation = new Annotation({
                position: [589748.27, 231444.54, 753.675],
                title: args.title ?? 'Place Tag',
                description: args.title ? '' : `Tag Description`,
                deviceType: false,
                panoramaImageId: panoramaImageId ? panoramaImageId : null,
                type: {
                    backgroundColor: '#00ff',
                },
            });
        }

        this.dispatchEvent({ type: 'start_inserting_annotation', annotation: annotation });

        annotations.add(annotation);

        annotations.children.map((annot) => annot.disableMouseEvents());

        let insertionCallback = async (e) => {
            let intersections = await updatePosition(e);
            if (intersections && (e.button === THREE.MOUSE.LEFT || e.type === 'touchend')) {
                annotation.objectLink = intersections.objectLink;
                requestAnimationFrame(() => this.callbacks.finish());
                annotations.children.map((annot) => annot.enableMouseEvents());
            } else if (e.button === THREE.MOUSE.RIGHT) {
                this.callbacks.cancel();
                annotations.children.map((annot) => annot.enableMouseEvents());
            } else if (!intersections) {
                annotations.children.map((annot) => annot.disableMouseEvents());
            }
        };
        this.callbacks.cancel = (e) => {
            this.isInserting = false;
            isDone = true;

            this.elCancelInsertion.style.display = 'none';
            if (!args.tagId) annotations.remove(annotation);

            domElement.removeEventListener('click', insertionCallback, true);
            domElement.removeEventListener('contextmenu', insertionCallback, true);
            domElement.removeEventListener('touchend', insertionCallback, true);
            domElement.removeEventListener('mousemove', updatePosition, false);
            annotation.domElement[0].removeEventListener('contextmenu', insertionCallback, true);

            args.addAnnotationCancel && args.addAnnotationCancel(annotation);
        };

        this.callbacks.finish = (e) => {
            this.isInserting = false;
            isDone = true;

            this.elCancelInsertion.style.display = 'none';
            domElement.removeEventListener('click', insertionCallback, true);
            domElement.removeEventListener('contextmenu', insertionCallback, true);
            domElement.removeEventListener('touchend', touchCallback, false);
            domElement.removeEventListener('mousemove', updatePosition, false);
            annotation.domElement[0].removeEventListener('contextmenu', insertionCallback, true);
            args.addAnnotationFinish && args.addAnnotationFinish(annotation);
        };

        domElement.addEventListener('click', insertionCallback, true);
        domElement.addEventListener('contextmenu', insertionCallback, true);
        annotation.domElement[0].addEventListener('contextmenu', insertionCallback, true);

        const getMouse = (e) => {
            if (e.changedTouches) {
                const rect = viewer.renderer.domElement.getBoundingClientRect();
                let x = e.changedTouches[0].pageX - rect.left;
                let y = e.changedTouches[0].pageY - rect.top;

                return { x, y };
            }

            return { x: e.offsetX, y: e.offsetY };
        };

        let updatePosition = async (e) => {
            let allIntersections = [];

            const mouse = getMouse(e);

            let pointCloudIntersection = Utils.getMousePointCloudIntersection(
                mouse,
                this.viewer.scene.getActiveCamera(),
                this.viewer,
                this.viewer.scene.pointclouds,
                { pickClipped: true, pickInvisible: false }
            );
            if (pointCloudIntersection) {
                allIntersections.push({ objectLink: pointCloudIntersection.pointcloud.userData.objectLink, ...pointCloudIntersection });
            }
            const volumeArray = [...this.viewer.scene.volumes, ...this.viewer.scene.planes];

            if (volumeArray.length > 0) {
                let volumeIntersection = Utils.getMeshesIntersectionWithVolumes(
                    mouse,
                    this.viewer.scene.getActiveCamera(),
                    this.viewer,
                    volumeArray,
                    { pickClipped: true, pickInvisible: false }
                );
                if (volumeIntersection) {
                    allIntersections.push(volumeIntersection);
                }
            }

            // Add library mesh intersection check
            if (this.viewer.scene.meshes && this.viewer.scene.meshes.length > 0) {
                let libraryMeshIntersection = Utils.getMeshesIntersectionWithLibraryMesh(
                    mouse,
                    this.viewer.scene.getActiveCamera(),
                    this.viewer,
                    this.viewer.scene.meshes,
                    { pickClipped: true, pickInvisible: false }
                );
                if (libraryMeshIntersection) {
                    allIntersections.push(libraryMeshIntersection);
                }
            }

            if (args.ifcHelper) {
                let ifcPoint = await args.ifcHelper.castRayIfc(false, true);
                if (isDone) return false;

                if (ifcPoint) {
                    let objLink = findObjectLink(ifcPoint.object);
                    let ifcIntersection = { objectLink: objLink, location: ifcPoint.point, distance: ifcPoint.distance, ifcPoint };
                    allIntersections.push(ifcIntersection);
                }
            }

            allIntersections.sort((a, b) => a.distance - b.distance);

            if (allIntersections.length > 0) {
                let I = allIntersections[0];
                if (I.ifcPoint?.object?.isFrag) {
                    annotation.ifcId = I.ifcPoint.guid;
                    annotation.fileName = I.ifcPoint.fileName;
                    annotation.ifcType = 'FragmentMesh';
                }

                let expressID = args.ifcHelper && I.ifcPoint ? args.ifcHelper.getExpressID(I.ifcPoint) : null;
                if (!expressID && I.ifcPoint) {
                    expressID = I.ifcPoint.guid;
                }
                const customAssetUuid = I?.volumeMesh?.uuid ?? I?.libraryMesh?.uuid ?? null;

                if (!(args.tagId && args.ifcId) || expressID === args.ifcId || customAssetUuid === args.ifcId) {
                    this.s.position.copy(I.location);
                    annotation.position.copy(I.location);
                }

                if (I.volumeMesh) {
                    annotation.ifcType = 'VolumeMesh';
                } else if (I.libraryMesh) {
                    annotation.ifcType = 'LibraryMesh';
                }

                return I;
            }

            return false;
        };

        function findObjectLink(obj) {
            // Base case: If obj is null or undefined, return undefined
            if (!obj) return undefined;

            // Check if objectLink exists in the current object
            if (obj.userData && obj.userData.objectLink !== undefined) {
                return obj.userData.objectLink;
            }

            // Otherwise, recursively check the parent object
            return findObjectLink(obj.parent);
        }

        const touchCallback = (e) => {
            let intersections = updatePosition(e);
            if (intersections) insertionCallback(e);
        };

        domElement.addEventListener('mousemove', updatePosition, false);
        domElement.addEventListener('touchend', touchCallback, false);

        this.viewer.scene.scene.add(this.s);

        return annotation;
    }

    isInsertionActive() {
        return this.isInserting;
    }

    createCancelInsertionButton() {
        let btn = document.createElement('input');
        btn.type = 'image';
        btn.title = 'Cancel Annotation Insertion';
        btn.id = 'cancelInsertion';
        btn.src = Potree.resourcePath + '/icons/remove.svg';
        btn.style.position = 'absolute';
        btn.style.left = '50%';
        btn.style.transform = 'translateX(-50%)';
        btn.style.bottom = '10px';
        btn.style.zIndex = '10000';
        btn.style.height = '36px';
        btn.style.width = '36px';
        btn.style.background = 'rgb(238, 238, 238, 0.8)';
        btn.style.borderRadius = '5px';
        btn.style.display = 'none';
        btn.setAttribute('data-html2canvas-ignore', '');
        btn.addEventListener('click', () => {
            this.callbacks.cancel && this.callbacks.cancel();
        });
        return btn;
    }

    update() {
        // let camera = this.viewer.scene.getActiveCamera();
        // let domElement = this.renderer.domElement;
        // let measurements = this.viewer.scene.measurements;
        // const renderAreaSize = this.renderer.getSize(new THREE.Vector2());
        // let clientWidth = renderAreaSize.width;
        // let clientHeight = renderAreaSize.height;
    }

    render() {
        //this.viewer.renderer.render(this.scene, this.viewer.scene.getActiveCamera());
    }
}
